import React, { useEffect, useState } from "react";
import SectionHeader from "./SectionHeader";
import { dashboardAlerts } from "../mock";
import { Radar, AlertTriangle, ShieldAlert, CheckCircle2, Filter } from "lucide-react";

const sevConfig = {
  HIGH: { cls: "sev-high", bg: "bg-sev-high", bd: "border-sev-high" },
  MED: { cls: "sev-med", bg: "bg-sev-med", bd: "border-sev-med" },
  LOW: { cls: "sev-low", bg: "bg-sev-low", bd: "border-sev-low" },
};

const Dashboard = () => {
  const [filter, setFilter] = useState("ALL");
  const [counts, setCounts] = useState({ HIGH: 0, MED: 0, LOW: 0 });
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const c = { HIGH: 0, MED: 0, LOW: 0 };
    dashboardAlerts.forEach((a) => (c[a.sev] += 1));
    setCounts(c);
  }, []);

  useEffect(() => {
    const i = setInterval(() => setPulse((p) => (p + 1) % 100), 1800);
    return () => clearInterval(i);
  }, []);

  const filtered = filter === "ALL" ? dashboardAlerts : dashboardAlerts.filter((a) => a.sev === filter);

  const filters = ["ALL", "HIGH", "MED", "LOW"];

  return (
    <section id="dashboard" className="relative px-6 lg:px-10 py-20 border-t border-[#1c2533]">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          num="05 // OPS CONSOLE"
          title="SOC DASHBOARD"
          subtitle="ACTIVE ALERT QUEUE // SOC-L1-ANALYST"
        />

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { icon: ShieldAlert, label: "HIGH SEVERITY", value: counts.HIGH, color: "text-red-400" },
            { icon: AlertTriangle, label: "MED SEVERITY", value: counts.MED, color: "text-amber-400" },
            { icon: Radar, label: "LOW SEVERITY", value: counts.LOW, color: "text-cyan-400" },
            { icon: CheckCircle2, label: "RESOLVED 24H", value: 12, color: "text-emerald-400" },
          ].map((s, i) => {
            const Ico = s.icon;
            return (
              <div key={i} className="border border-[#1c2533] bg-[#0c1118] p-4 card-hover">
                <div className="flex items-center justify-between">
                  <Ico size={18} className={s.color} strokeWidth={1.5} />
                  <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500">LIVE</span>
                </div>
                <div className={`font-mono text-3xl font-bold mt-3 ${s.color}`}>
                  {String(s.value).padStart(2, "0")}
                </div>
                <div className="font-mono text-[10px] tracking-[0.18em] text-slate-400 mt-1">
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Alert table */}
        <div className="border border-[#1c2533] bg-[#0c1118] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#1c2533] bg-[#0a0e14]">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
              <span className="font-mono text-[11px] tracking-[0.2em] text-slate-200">
                LIVE SIMULATION
              </span>
              <span className="font-mono text-[10px] tracking-[0.15em] text-slate-500">
                SYNC {pulse}%
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Filter size={12} className="text-slate-500 mr-1" />
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`font-mono text-[10px] tracking-[0.18em] px-2.5 py-1 rounded-sm border transition-colors ${
                    filter === f
                      ? "border-cyan-400 text-cyan-400 bg-cyan-400/5"
                      : "border-[#1c2533] text-slate-400 hover:text-slate-100"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="font-mono text-[10px] tracking-[0.2em] text-slate-500 border-b border-[#1c2533]">
                  <th className="px-5 py-3 font-normal">TIMESTAMP</th>
                  <th className="px-5 py-3 font-normal">ALERT NAME</th>
                  <th className="px-5 py-3 font-normal">SEVERITY</th>
                  <th className="px-5 py-3 font-normal">STATUS</th>
                  <th className="px-5 py-3 font-normal">SOURCE</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => {
                  const cfg = sevConfig[a.sev];
                  return (
                    <tr
                      key={i}
                      className="border-b border-[#1c2533] last:border-0 hover:bg-[#131c28] transition-colors"
                    >
                      <td className="px-5 py-3.5 font-mono text-[12px] text-slate-400">{a.ts}</td>
                      <td className="px-5 py-3.5 text-[13px] text-slate-100">{a.name}</td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`status-badge ${cfg.bg} ${cfg.bd} ${cfg.cls}`}
                        >
                          <span className="pulse-dot">●</span> {a.sev}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 font-mono text-[11px] tracking-[0.12em] text-cyan-400">
                        {a.status}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-[12px] text-slate-300">
                        {a.source}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
